import { Component, type ReactNode } from "react";

type Failure = ReactNode | ((error: unknown) => ReactNode);

/**
 * Catches a failed dynamic import (#997, #1678).
 *
 * A stale tab after a deploy can 404 the chunk it asks for, because the old
 * hashed file is gone. `React.lazy` surfaces that as a render error, and
 * without a boundary it would unmount everything above it — the whole shell,
 * for a per-tab view. The boundary renders `failure` instead, which is where a
 * reload prompt belongs.
 *
 * `failure` may be a function of the error (September audit): the shell's
 * fallback tells a lost connection, a deploy under the tab, a request that did
 * not get through and a render error apart, and it can only do that with the
 * error in hand. A plain node still works for callers with one answer.
 */
export class ChunkBoundary extends Component<{ children: ReactNode; failure: Failure }, { failed: boolean; error: unknown }> {
  state: { failed: boolean; error: unknown } = { failed: false, error: undefined };
  static getDerivedStateFromError(error: unknown) {
    return { failed: true, error };
  }
  render() {
    if (this.state.failed) {
      const { failure } = this.props;
      return typeof failure === "function" ? failure(this.state.error) : failure;
    }
    return this.props.children;
  }
}
